import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, MapPin, Instagram, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast({
        title: "Mesaj Gonderildi!",
        description: "En kisa surede size donus yapacagiz."
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Hata",
        description: "Mesaj gonderilemedi. Lutfen tekrar deneyin.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">İletişim</h1>
          <p className="text-xl text-gray-600">
            Sorularınız veya projeleriniz için bizimle iletişime geçin
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <Card className="border-none shadow-xl">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">İletişim Formu</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-base font-semibold">Ad Soyad</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Adınız ve soyadınız"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-2 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-base font-semibold">E-posta</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="ornek@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="mt-2 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-base font-semibold">Telefon</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="0555 123 45 67"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="mt-2 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base font-semibold">Mesajınız</Label>
                    <Textarea
                      id="message"
                      placeholder="Mesajınızı buraya yazın..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      className="mt-2 min-h-32"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {loading ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Telefon</h3>
                    <a href="tel:05516380934" className="text-teal-600 hover:text-teal-700 text-lg font-semibold">
                      0551 638 09 34
                    </a>
                    <p className="text-sm text-gray-600 mt-1">7/24 Ulaşabilirsiniz</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">E-posta</h3>
                    <a href="mailto:koctechiklimlendirme@gmail.com" className="text-teal-600 hover:text-teal-700 break-all">
                      koctechiklimlendirme@gmail.com
                    </a>
                    <p className="text-sm text-gray-600 mt-1">24 saat içinde cevap veriyoruz</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Adres</h3>
                    <p className="text-gray-700">
                      Turgut Özal Mahallesi 2141 Cadde No:4-9 Dükkan 12
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Instagram</h3>
                    <a
                      href="https://instagram.com/koctechiklimlendirme"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:text-teal-700"
                    >
                      @koctechiklimlendirme
                    </a>
                    <p className="text-sm text-gray-600 mt-1">Bizi takip edin</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/905516380934"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-green-500 to-green-600 cursor-pointer group">
                <CardContent className="p-6">
                  <div className="flex items-center justify-center space-x-3 text-white">
                    <MessageCircle className="w-8 h-8 group-hover:scale-110 transition-transform duration-300" />
                    <span className="text-xl font-bold">WhatsApp ile İletişime Geç</span>
                  </div>
                </CardContent>
              </Card>
            </a>
          </div>
        </div>

        {/* Google Maps */}
        <Card className="mt-12 border-none shadow-xl overflow-hidden">
          <CardContent className="p-0">
            <div className="relative w-full h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3060.363076891843!2d32.8!3d39.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDU0JzAwLjAiTiAzMsKwNDgnMDAuMCJF!5e0!3m2!1sen!2str!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Koctech Klima Konum"
                className="absolute inset-0"
              />
            </div>
            <div className="p-6 bg-white">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Bizi Ziyaret Edin</h3>
              <p className="text-gray-600">
                Turgut Ozal Mahallesi 2141 Cadde No:4-9 Dukkan 12
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Working Hours */}
        <Card className="mt-12 border-none shadow-lg bg-gradient-to-br from-gray-50 to-white">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">Çalışma Saatlerimiz</h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="text-center">
                <h4 className="font-bold text-gray-900 mb-2">Hafta İçi</h4>
                <p className="text-gray-600">Pazartesi - Cuma</p>
                <p className="text-teal-600 font-semibold text-lg">08:00 - 19:00</p>
              </div>
              <div className="text-center">
                <h4 className="font-bold text-gray-900 mb-2">Hafta Sonu</h4>
                <p className="text-gray-600">Cumartesi - Pazar</p>
                <p className="text-teal-600 font-semibold text-lg">09:00 - 17:00</p>
              </div>
            </div>
            <p className="text-center text-gray-600 mt-6">
              <span className="font-semibold text-teal-600">Acil servis:</span> 7/24 hizmetinizdeyiz
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Contact;